import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

type Props = {
  code: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ReportErrorDialog({ code, open, onOpenChange }: Props) {
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("error_reports")
        .update({
          reported_by_user: true,
          user_description: description.trim() || null,
        })
        .eq("code", code);

      if (error) throw error;

      toast({
        title: "Merci pour votre signalement",
        description: "Notre équipe va analyser cette erreur au plus vite.",
      });
      setDescription("");
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Impossible d'envoyer le signalement",
        description: e?.message ?? "Réessayez plus tard.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Signaler cette erreur à l'admin</DialogTitle>
          <DialogDescription>
            Code d'erreur : <code className="font-mono bg-muted px-1.5 py-0.5 rounded">{code}</code>
            <br />
            Décrivez ce que vous étiez en train de faire — cela nous aide à corriger le problème plus rapidement.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Ex : J'ai cliqué sur « Payer » et rien ne s'est passé…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          maxLength={1000}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
